/**
 * @file dcp/src/dcp-client/worker/supervisor2/slice2.js
 *
 * A wrapper for the slice object returned from the scheduler's task distributor.
 *
 * @author Matthew Palma, mpalma@kingsds.network
 *         Ryan Rossiter, ryan@kingsds.network
 *         Wes Garland, wes@distributive.network
 *         Paul, paul@distributive.network
 * @date May 2019
 *       May 2019
 *       Decemeber 2020
 *       June 2022, Feb-April 2023
 * @module slice
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
// @ts-check
'use strict';

const { Synchronizer } = require('dcp/common/concurrency');
const { assert } = require('dcp/common/dcp-assert');
const debugging = require('dcp/debugging').scope('worker');
const common = require('./common');

const INITIAL  = 'INITIAL';
const READY    = 'READY';
const RESERVED = 'RESERVED';
const WORKING  = 'WORKING';
const WORKDONE = 'WORKDONE';
const COMPLETE = 'COMPLETE';
const FAILED   = 'FAILED';
const FINISHED = 'FINISHED';

/** @typedef {string} opaqueId */ // 22 character base64 string
/** @typedef {import('./job-manager').JobHandle} JobHandle               */
/** @typedef {import('./job-manager').JobManager} JobManager             */
/** @typedef {import('dcp/utils/jsdoc-types').AuthMessage} AuthMessage   */
/** @typedef {import('dcp/utils/jsdoc-types').SliceMessage} SliceMessage */

/** 
 * Object use to represent a given slice inside the Supervisor. This object's shape 
 * current inherits heavily from the message payload originating from the scheduler,
 * but should not /wg dec 2020.
 *
 * Caveat lector: this documentation was created without a 100% understanding of the code.
 *                Please improve when possible.
 * 
 * The read-only properties of this object are as follows:
 * - state                      INITIAL | READY | RESERVED | WORKING | WORKDONE | COMPLETE | FAILED | FINISHED
 * - sliceNumber                the number of the slice within this job
 * - jobId                      the id of the job that this slice belongs to
 * - isEstimation               true when slice is used in estimation
 * - isLong                     true when slice execution longer than supervisor.options.targetTaskDurationMs ms
 * - datum                      input set element for this slice this slice of the job; could be a data: URI or it could
 *                               be a URL we need to fetch; note that fetches  are limited to worker's allow list
 * - result
 * - result.request            'complete',...
 * - result.result              return value from work function
 * - result.timeReport          { total, CPU, webGL } ms
 * - error                      error info when slice FAILED
 * - jobManager                 wrapper for the job that owns this slice
 * - resultStorageType          'values'  => we are storing individual values,
 *                                           which could be data: URIs or URLs
 *                                           at the scheduler
 *                              'pattern' => user-specified pattern for result URLs.
 *                                           Data will be uploaded via POST to the
 *                                           URL matching the pattern, provided the
 *                                           worker is allowed to access it.
 * - resultStorageParams        user-supplied POST parameters sent to the result 
 *                               storage server when using resultStorageType = pattern.
 * - resultStorageDetails       the pattern when using resultStorageType = pattern.
 * - authorizationMessage       authorization from task distributor, sent to result submitter, etc...
 * 
 * - isUnassigned               true, when in state INITIAL
 * - isReady                    true, when in state READY
 * - isReserved                 true, when in state RESERVED
 * - isWorking                  true, when in state WORKING
 * - isWorkDone                 true, when in state WORKDONE <-- work has completed
 * - isComplete                 true, when in state COMPLETE <-- work has completed successfully
 * - isFailed                   true, when in state FAILED   <-- work has failed
 * - isFinished                 true, when in state FINISHED <-- Indicates result submission has succeeded.
 *
 * - isQueued                   true, when in state INITIAL, READY
 * - isActive                   true, when in state RESERVED, WORKING, WORKDONE, COMPLETE
 * - identifier                 string 'sliceNumber.jobId.state'
 * - timeReport                 accessor for this.result.timeReport that updates from this.rejectedState when appropriate
 *
 * The r/w properties of this object are as follows:
 * - startTime                  time when slice execution started
 * - rejectedState              work.reject internal state
 * - inputDataSize              serialized size of #datum
 * 
 * NOTE: If you ever use a property with a leading underscore you are probably making a mistake.
 *       But if you must, please ask paul, yarn, bryan or eddie for a CR.
 *
 * INITIAL  -> READY:    - when slice data has been successfully fetched
 * INITIAL  -> BROKEN:   - when slice data has not been fetched successfully
 * READY    -> RESERVED: - when a slice has been scheduled to run on a sandbox
 * RESERVED -> WORKING:  - right before Sandbox2.work is called
 * WORKING  -> WORKDONE: - right after Sandbox2.work completes successfully or not
 * WORKDONE -> COMPLETE: - when Slice2.collectResults is called and Sandbox2.work completed successfully
 * WORKDONE -> FAILED:   - when Slice2.collectResults is called and Sandbox2.work did not completed successfully
 * COMPLETE -> FINISHED: - when submitting results succeeds
 * COMPLETE -> FAILED:   - when submitting results fails
 */
class Slice
{
  /** @type {Promise<void>} */
  #fetchSliceDataPromise;
  /** @type {AuthMessage} */
  #authorizationMessage;
  // private objects
  #datum;
  #result;
  #error;

  /**
   * @param {JobManager} jobManager
   * @param {SliceMessage} sliceMessage
   * @param {AuthMessage} authorizationMessage
   * @param {boolean} [fetchSlices=true]
   */
  constructor (jobManager, sliceMessage, authorizationMessage, fetchSlices = true)
  {
    /** @type {SliceMessage} */
    this.sliceMessage = { ...sliceMessage }; /** XXXpfr @todo Do we have to clone the object? Pretty sure we don't. */
    /** @type {number} */
    this.sliceNumber  = this.sliceMessage.sliceNumber;
    /** @type {JobManager} */
    this.jobManager   = jobManager;
    /** @type {string} */
    this.jobId   = this.jobManager.jobId;
    /** @type {Synchronizer} */
    this.state = new Synchronizer(INITIAL, [ INITIAL, READY, RESERVED, WORKING, WORKDONE, COMPLETE, FAILED, FINISHED ]);

    /** @type {number} */
    this.startTime = 0;
    /** @type {number} */
    this.inputDataSize = 0;
    /** @type {boolean} */
    this.overdue = false;
    /** @type {number} */
    this.gpuOverage = 0;
    /** @type {{CPUTime: number, GPUTime: number, CPUDensity: number, GPUDensity: number, InDataSize: number, OutDataSize: number}} */
    this.metrics = null;
    /** @type {{ retry: number, report: { total: number, CPU: number, webGL: number, webGPU: number, InDataSize: number, OutDataSize: number }, info: {message: string|false, retries: number, name: string} }} */
    this.rejectedState = null;

    this.#fetchSliceDataPromise = Promise.resolve();
    this.#authorizationMessage  = authorizationMessage;
    this.#datum                 = null;
    this.#result                = null;
    this.#error                 = null;

    assert(this.jobId === String(this.sliceMessage.jobId));
    if (!this.#authorizationMessage)
      throw new Error(`Undefined authorization for slice ${this.identifier}.`);

    /** 
     * Start loading slice datum in the background. Once these are loaded, slice state will
     * transition to READY and the slice will be ready to transition to WORKING.
     */
    if (fetchSlices)
      this.#fetchSliceDataPromise = this.fetchSliceData();
  }

  /** @type {number} */
  get cotaskCount ()  { return this.sliceMessage.cotaskCount; }
  /** @type {boolean} */
  get isEstimation () { return this.sliceMessage.isEstimationSlice; }
  /** @type {boolean} */
  get isLong ()       { return this.sliceMessage.isLongSlice; }
  /** @type {string} */
  get datumUri ()     { return this.sliceMessage.datumUri; }
  /** @type {JobHandle} */
  get jobHandle ()    { return this.jobManager.jobHandle; }
  /** @type {string} */
  get resultStorageType () { return this.sliceMessage.resultStorageType; }
  /** @type {string} */
  get resultStorageDetails () { return this.sliceMessage.resultStorageDetails; }
  /** @type {Promise<void>} */
  get fetchSliceDataPromise () { return this.#fetchSliceDataPromise; }
  /** @type {AuthMessage} */
  get authorizationMessage () { return this.#authorizationMessage; }

  /** Read-only properties of type object. */
  get datum ()                { return this.#datum; }
  get result ()               { return this.#result; }
  get error ()                { return this.#error; }
  get resultStorageParams ()  { return this.sliceMessage.resultStorageParams; }

  /** @type {boolean} */
  get isUnassigned () { return this.state.is(INITIAL); }
  /** @type {boolean} */
  get isReady ()      { return this.state.is(READY); }
  /**
   * Mark a slice as RESERVED to remove it from the ready list, yet still able to transition to WORKING.
   * @type {boolean}
   **/
  get isReserved ()   { return this.state.is(RESERVED); }
  /** @type {boolean} */
  get isWorking ()    { return this.state.is(WORKING); }
  /** @type {boolean} */
  get isWorkDone ()   { return this.state.is(WORKDONE); }
  /** @type {boolean} */
  get isComplete ()   { return this.state.is(COMPLETE); }
  /** @type {boolean} */
  get isFailed ()     { return this.state.is(FAILED); }
  /** @type {boolean} */
  get isFinished ()   { return this.state.is(FINISHED); }
  /** @type {boolean} */
  get isQueued ()     { return this.isUnassigned || this.isReady; }
  /** @type {boolean} */
  get isActive ()     { return this.isReserved || this.isWorking || this.isWorkDone || this.isComplete; }
  /** @type {boolean} */
  get isQueuedOrActive () { return !(this.isFailed || this.isFinished); }

  /** @type {string} */
  get identifier () {
    return `${this.sliceNumber}.${this.jobId}.${this.state}`;
  }

  /** 
   * timeReport accessor for the CG part of CGIO.
   * @type {{ total: number, CPU: number, webGL: number, webGPU: number }}
   **/
  get timeReport ()
  {
    if (!this.result?.timeReport)
      throw new Error('Slice timeReport is not ready'); // Should never fire.
    return this.result.timeReport;
  }
  /**
   * dataReport accessor for the IO part of CGIO.
   * @type {{ InDataSize: number, OutDataSize: number }}
   **/
  get dataReport ()
  {
    if (!this.result?.dataReport)
      throw new Error('Slice dataReport is not ready'); // Should never fire.
    return this.result.dataReport;
  }
  /**
   * Return the estimated elapsed time to slice completion time.
   * @type {number}
   **/
  get etaMs ()
  {
    if (this.startTime === null) return 0; // slice is either COMPLETE, FINISHED or FAILED
    return Math.max(0, this.jobManager.emaCPUTime - (Date.now() - this.startTime));
  }

  /**
   * Syntax: work.reject(stringReason, retries = 0)
   *  - Old work.reject(stringReason) is equivalent to work.reject(stringReason, 0)
   *  - Old work.reject(false) is equivalent to work.reject(false, 1)
   *
   * @param {{ reason: string|false, retries: number }} data
   * @param {{ total : number, CPU: number, webGL: number, webGPU: number }} timeReport
   * @param {number} InDataSize
   * @param {number} OutDataSize
   * @returns {{message: string|false, retries: number, name: string}} -- work.reject info
   */
  constructRejectedState (data, timeReport, InDataSize, OutDataSize)
  {
    common.selectiveDebug2() && console.debug('Slice.constructRejectedState-begin:', data, timeReport, InDataSize, OutDataSize);
    if (this.rejectedState)
    {
      common.accumulateTime(this.rejectedState.report, timeReport);
      this.rejectedState.report.InDataSize  += InDataSize;
      this.rejectedState.report.OutDataSize += OutDataSize;
    }
    else
    {
      let retries = data.retries;
      assert(typeof retries === 'number' && retries >= 0);
      this.rejectedState = {
        /** @type {number} */
        retry: 0,
        /** @type {{ total: number, CPU: number, webGL: number, webGPU: number, InDataSize: number, OutDataSize: number }} */
        report: {
          total:  timeReport.total,
          CPU:    timeReport.CPU,
          webGL:  timeReport.webGL,
          webGPU: timeReport.webGPU,
          InDataSize,
          OutDataSize,
        },
        info: {
          message: data.reason,
          retries,
          name: 'EWORKREJECT',
        },
      };
    }
    common.selectiveDebug() && console.debug('Slice.constructRejectedState-end:', this.rejectedState);
    return this.rejectedState.info;
  }

  /**
   * Clone a WORKDONE slice to re-execute.
   * Used in handleWorkReject.
   */
  cloneReady ()
  {
    assert(this.isWorkDone);
    const clonedSlice = new Slice(this.jobManager, { ...this.sliceMessage }, this.authorizationMessage, false);
    clonedSlice.#datum = this.datum;
    clonedSlice.startTime = Date.now();
    clonedSlice.inputDataSize = this.inputDataSize;
    clonedSlice.rejectedState = this.rejectedState;
    clonedSlice.state.set(INITIAL, READY);
    return clonedSlice;
  }

  /**
   * Need the ability to force WORKDONE in the catch handlers in sandbox.work.
   * DANGER: Do not call except in the catch handlers in sandbox.work.
   */
  forceWorkDone ()
  {
    if (this.isWorkDone) return;
    this.state.set([ INITIAL, READY, RESERVED, WORKING, COMPLETE, FAILED, FINISHED ], WORKDONE);
  }

  /**
   * Remove slice from the ready list: READY --> RESERVED.
   */
  markAsReserved ()
  {
    this.state.set(READY, RESERVED);
    this.startTime = Date.now(); // A reserved slice has a finite lifetime (e.g. 2 minutes.)
  }

  /** Sets the slice status to WORKING, called when the slice is getting ready to be handed to a worker. */
  markAsWorking ()  { this.state.set(RESERVED, WORKING); }

  /**
   * Sets the slice status to WORKDONE, called in sandbox.work when slice has finished executiion.
   * This needs to be set before the sandbox 'complete' event fires, or else supervisor.roundRobinSlices
   * may miss scheduling another slice on the empty sandbox slot.
   **/
  markAsWorkDone()  { this.state.set(WORKING, WORKDONE); }

  /** Sets the slice status to FINISHED, called when the slice has completed and submitted results.  */
  markAsFinished()  { this.state.set(COMPLETE, FINISHED); }

  /** Sets the slice status to FINISHED, called when the slice is being returned to scheduler by Supervisor.repoMan.  */
  repoMan()         { this.state.set(READY, FINISHED); }

  /** Hide the result when returning slices. */
  killResult ()     { this.#result = null; }

  /** Use when deciding to not schedule a GPU slice when an assigned sandbox cannot be created. */
  clearGPUState ()  { this.gpuOverage = 0; }

  /**
   * Something went wrong, but we want to reuse the slice.
   * However, this may have already been done, so conditionalize.
   * E.g. Supervisor.evaluatorEnd calls slice.tryRecoverSlice which may happen before other error handling.
   */
  resetFromReserved ()
  {
    common.selectiveSupEx() && console.debug(`Slice.resetFromReserved: slice ${this.identifier}`);
    if (!this.isReady)
      this.state.set(RESERVED, READY);
  }

  /**
   * In certain circumstances we need to start over with slice scheduling.
   * The primary example is the evaluator kills the sandbox after both the slice and sandbox
   * are paired up and RESERVED or WORKING.  We detect evaluator killing of a sandbox via
   * the evaluatorHandle 'end' event handler.
   * @returns {boolean} -- false when unable to transition slice to READY
   */
  tryRecoverSlice ()
  {
    common.selectiveSupEx() && console.debug(`Slice.tryRecoverSlice: slice ${this.identifier}`);
    if (this.isReserved)
      this.resetFromReserved();
    else if (this.isWorking)
      this.state.set(WORKING, READY);
    else
      return false;
    return true;
  }

  /**
   * Fetch slice data.
   * @returns {Promise<void>}
   */
  async fetchSliceData()
  {
    this.startTime = Date.now();
    return this.jobManager.fetchSliceData(this.datumUri, this)
      .then ((sliceData) => {
        debugging('slice') && console.debug(`Slice ${this.identifier} is transitioning to READY; slice datum fetch timespan ${Date.now() - this.startTime}`);
        this.inputDataSize = sliceData.size;
        this.#datum = sliceData.data;
        this.state.set(INITIAL, READY);
      })
      .catch((error) => {
        common.selectiveDebug() && console.debug(`fetchSliceData failed for slice ${this.identifier}`, error);
        this.#error = error;
        this.jobManager.returnSlice(this, common.errorCodeOrString(error, `fetchSliceData: ${error.message}`));
        this.state.set(INITIAL, FAILED);
        throw error;
      });
  }

  /**
   * Receives a result from the scheduler.
   * When success is true, it will set the result to this.#result.
   * When success is false, it will set the error to this.#error.
   * If this.#result is populated, it is possible to set the error, but setting the result will throw.
   * If this.#error is populated, then collectResult cannot be called w/o emitting an exception.
   *
   * @param {object|Error} result         - The result from the worker sandbox or an error.
   * @param {boolean}      [success=true] - True if result is considered successful, false if error occurred.
   */
  collectResult(result, success = true)
  {
    debugging('slice') && console.debug(`collectResult for slice ${this.identifier}`, success, this.error);

    this.startTime = null;
    if (success && this.result)
      throw new Error(`Slice ${this.identifier} already has a result.`);
    if (this.error)
      throw new Error(`Slice ${this.identifier} already has an error property.`);

    if (success)
    {
      this.state.set(WORKDONE, COMPLETE); // Work completed successfully.
      this.#result = result;
    }
    else
    {
      this.state.set([ RESERVED, WORKING, WORKDONE, COMPLETE ], FAILED);
      this.#error = result;
    }
  }

  /**
   * Create basic message object as part of the payload to send back to the result-submitter's status operation.
   * @param {string}   status               - The kind of status operation
   * @param {object}   [extraProperties={}] - Extra properties for the payload object.
   * @returns {object}
   */
  getMessage(status, extraProperties = {})
  {
    return {
      sliceNumbers:         [this.sliceNumber],
      job:                  this.jobId,
      authorizationMessage: this.authorizationMessage,
      status,
      ...extraProperties,
    }; 
  }

}
exports.Slice = Slice;
